<script setup lang="ts">
import type { Project } from '~/utils/project/Project'
import { nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ComboConecteModal from '~/components/simulation/ComboConecteModal.vue'
import { FEATURE_FLAGS } from '~/constants'
import SimulatorService from '~/services/simulator/Simulator'
import { useFeatureFlag } from '~/store-v2/feature-flag/useFeatureFlag'
import useFinancing from '~/store-v2/financings/useFinancing'
import useProjectContractSigned from '~/store-v2/projects/useProjectContractSigned'
import { getErrorMessage } from '~/utils/errors'
import {
  calculateDefaultInstallmentForIncompleteProject,
  createSimulationOpportunitiesPayload,
  extractProjectDefaults,
  isProjectComplete,
  isProjectInactive,
} from '~/utils/simulation/projects'

const router = useRouter()
const route = useRoute<any>()
const { d } = useI18n()

const id = computed(() => route.params.id as string)
const {
  data: financing,
  error,
  isError,
  isLoading,
} = useFinancing(id)

const {
  data: isProjectContractSigned,
} = useProjectContractSigned(id)

const isBiometrySubmitted = computed(() => {
  return !!financing.value?.section_statuses?.biometry_and_contract?.metadata?.biometry?.last_submitted_at
})

const canEditSimulation = computed(() => {
  return !isProjectContractSigned.value && !isBiometrySubmitted.value
})

const projectResponse = ref<Project | undefined>(undefined)
const loadingProject = ref(false)
const simulation = ref({})
const projectIsFinished = ref(false)
const project = ref<Project | null>(null)
const showComboConnectModal = ref(false)
const comboConecteModalInitialTab = ref<'benefits' | 'inverters'>('benefits')

const defaultCommission = ref(0)
const defaultCommissionRaw = ref(0)
const defaultGracePeriod = ref(1)
const defaultDueDay = ref<number | null>(null)
const defaultFirstDueDate = ref<string | null>('')
const defaultInstallment = ref(12)
const showCommissionMoney = ref(false)

const riskRioGrandeDoSul = 9

const { data: solfacilPlusPointsFeatureFlag } = useFeatureFlag<boolean>(FEATURE_FLAGS.SHOW_SOLFACIL_PLUS_POINTS, false)
const { data: showCommissionMoneyFeatureFlag } = useFeatureFlag<boolean>(FEATURE_FLAGS.SHOW_COMMISSION_MONEY, false)
const { data: enableDynamicDueDayData } = useFeatureFlag<boolean>(
  FEATURE_FLAGS.ENABLE_DYNAMIC_DUE_DAY,
  false,
  true,
)
const enableDynamicDueDay = computed(() => enableDynamicDueDayData.value === true)

const errorMessage = computed(() => getErrorMessage(error.value))
const loading = computed(() => loadingProject.value || isLoading.value)
const mappingErrors = computed(() => ['NOT_FOUND', 'UNAUTHORIZED', 'UNKNOWN'].includes(errorMessage.value))

const { setProject } = useResumeStore()
const { setSimulationOpportunities } = useResumeStore()

const simulatorService = new SimulatorService()

function openComboConnectModal(tab: 'benefits' | 'inverters' = 'benefits') {
  comboConecteModalInitialTab.value = tab
  showComboConnectModal.value = true
}

function mountNewQuotas(project: Project): void {
  const defaults = extractProjectDefaults(project, false, enableDynamicDueDay.value)

  defaultCommission.value = defaults.commission
  defaultCommissionRaw.value = defaults.commissionRaw
  defaultGracePeriod.value = defaults.gracePeriod
  defaultDueDay.value = defaults.dueDay
  defaultFirstDueDate.value = defaults.firstDueDate ? d(defaults.firstDueDate) : null
  defaultInstallment.value = defaults.installment
  projectIsFinished.value = defaults.isFinished
}

async function setOpportunities(project: Project): Promise<void> {
  if (!solfacilPlusPointsFeatureFlag.value) return

  try {
    const payload = createSimulationOpportunitiesPayload(project)
    const response = await simulatorService.post_simulation_opportunities(payload)

    if (response.data) {
      setSimulationOpportunities(response.data)
    }
  }
  catch (error) {
    console.error('Error setting opportunities:', error)
  }
}

async function loadRevisionProjectData(): Promise<void> {
  try {
    loadingProject.value = true

    const { data } = await simulatorService.get_project(route.params.id)
    if (data) {
      project.value = data
    }

    if (project.value && isProjectInactive(project.value)) {
      router.push('/projects')
      return
    }

    if (project.value && !project.value.complete) {
      defaultInstallment.value = calculateDefaultInstallmentForIncompleteProject(project.value)
    }

    if (project.value) {
      await setOpportunities(project.value)
      setProject(project.value)
      projectResponse.value = project.value
    }

    if (project.value && isProjectComplete(project.value)) {
      mountNewQuotas(project.value)
    }
  }
  catch (error) {
    console.error('Error loading revision project data:', error)
  }
  finally {
    loadingProject.value = false
  }
}

watch([financing, isProjectContractSigned], () => {
  if (financing.value && !isLoading.value && !canEditSimulation.value) {
    router.replace(`/simulation/details/${id.value}`)
  }
})

onMounted(async () => {
  await loadRevisionProjectData()

  await nextTick()
  if (financing.value && !isLoading.value && !canEditSimulation.value) {
    router.replace(`/simulation/details/${id.value}`)
  }
})

watch(showCommissionMoneyFeatureFlag, (value) => {
  showCommissionMoney.value = value || false
}, { immediate: true })

watch([enableDynamicDueDay, project], () => {
  if (!project.value || !isProjectComplete(project.value))
    return

  const defaults = extractProjectDefaults(project.value, false, enableDynamicDueDay.value)
  defaultDueDay.value = defaults.dueDay
})
</script>

<template>
  <div class="container">
    <NavigationHeaderBar
      stage="simulation"
      :title="projectResponse?.project_name"
      :financing="financing"
    />
    <div v-if="loading" class="installments-container-loader">
      <div class="loader-project z-[19px]" />
      <div class="loader-project hidden md:system:block z-[19px]" />
    </div>
    <template v-else>
      <template v-if="isError && mappingErrors">
        <SharedErrorsCheckAccess :error-message="errorMessage" />
      </template>
      <template v-else>
        <SolAlert
          v-if="projectResponse?.decision_pre_analisys === riskRioGrandeDoSul"
          id="title"
          class="mb-4xs md:site:mb-10"
          :title="$t('simulation.info_risk.RS_title')"
          feedback="warning"
        >
          <div>
            <p>{{ $t('simulation.info_risk.RS_description_conditions') }}</p>
            <ul class="list-disc list-inside">
              <li> {{ $t('simulation.info_risk.RS_description_grace') }} </li>
              <li> {{ $t('simulation.info_risk.RS_description_eligible_area') }} </li>
            </ul>
          </div>
        </SolAlert>
        <SimulationRevision
          v-if="projectResponse"
          :simulation="simulation"
          :disable-fields="!projectIsFinished"
          :project="projectResponse"
          :default-installment="defaultInstallment"
          :default-grace-period="defaultGracePeriod"
          :default-due-day="defaultDueDay"
          :default-first-due-date="defaultFirstDueDate"
          :default-commission="defaultCommission"
          :default-commission-raw="defaultCommissionRaw"
          :cet="project?.selected_installments?.cet || ''"
          :show-commission-money="showCommissionMoney"
          @open-combo-connect-modal="openComboConnectModal"
        />
        <div v-else class="my-4 card-container flex flex-col justify-between w-full align-middle py-lg text-center items-center">
          <img src="/images/not-found.png" class="not-found">
          <h1 class="fonts-heading-h1">
            Ops, algo deu errado
          </h1>
          <p class="fonts-subtitle-medium">
            Ocorreu algum problema e não conseguimos trazer o resultado da sua simulação. Tente novamente mais tarde
          </p>
        </div>
      </template>
    </template>
  </div>

  <ComboConecteModal
    v-model:is-open="showComboConnectModal"
    :project="projectResponse || null"
    :initial-tab="comboConecteModalInitialTab"
  />
</template>

<style lang="scss">
.container {
  @apply flex flex-col items-start px-micro pb-5 mt-6 gap-3;

  @screen md:system {
    @apply px-2xs;
  }
}

.installments-container-loader{
  @apply w-full block;
  @screen md:system {
    @apply grid grid-cols-6 gap-4;
    @apply grid gap-4;
    grid-template-columns: minmax(0, 1fr) 300px
  }
}

.loader-project {
  @apply h-96 rounded-lg mt-5;
  @apply top-0 right-0 bottom-0 left-0;
  background: rgba(#fff, 0.9)
    url(https://cdn.solfacil.com.br/assets/img/loading-for-legacy.gif) no-repeat
    center center;
}

.card-container{
  @apply px-12 my-6 bg-neutral-high-pure rounded-lg;

}
</style>
